import { z } from 'zod';
import i18n from '@/Config/app/i18n';
import {
  MetadataRunConditionDto,
  MetadataRunConditionOperator,
} from '@/Domain/api/Shared/dto/MetadataRunCondition.dto';

export const METADATA_CONDITION_VALUE_MAX_LENGTH = 500;
export const METADATA_CONDITION_VALUES_MAX_COUNT = 50;

export const metadataConditionUsesValue = (operator: MetadataRunConditionOperator): boolean =>
  operator === 'equals' || operator === 'not_equals' || operator === 'contains';

export const metadataConditionValuesSchema = () =>
  z
    .array(
      z
        .string()
        .min(1, i18n.t('metadata_conditions:values_required'))
        .max(
          METADATA_CONDITION_VALUE_MAX_LENGTH,
          i18n.t('metadata_conditions:value_too_long', {
            limit: METADATA_CONDITION_VALUE_MAX_LENGTH,
          }),
        ),
    )
    .max(
      METADATA_CONDITION_VALUES_MAX_COUNT,
      i18n.t('metadata_conditions:values_limit', { limit: METADATA_CONDITION_VALUES_MAX_COUNT }),
    )
    .transform((values) => Array.from(new Set(values)))
    .optional();

export const validateMetadataConditionOperand = (
  condition: { operator: MetadataRunConditionOperator; value?: string | null; values?: string[] },
  context: z.RefinementCtx,
): void => {
  if (condition.operator === 'is_one_of' && !condition.values?.length) {
    context.addIssue({
      code: 'custom',
      path: ['values'],
      message: i18n.t('metadata_conditions:values_required'),
    });
  }
  if (condition.operator === 'contains' && !condition.value?.length) {
    context.addIssue({
      code: 'custom',
      path: ['value'],
      message: i18n.t('metadata_conditions:value_required'),
    });
  }
};

export const changeMetadataConditionOperator = (
  condition: Pick<MetadataRunConditionDto, 'operator' | 'value' | 'values'>,
  operator: MetadataRunConditionOperator,
): Pick<MetadataRunConditionDto, 'operator' | 'value' | 'values'> => ({
  operator,
  value: metadataConditionUsesValue(operator)
    ? (condition.value ?? condition.values?.[0] ?? '')
    : null,
  values:
    operator === 'is_one_of'
      ? condition.values?.length
        ? condition.values
        : condition.value
          ? [condition.value]
          : []
      : [],
});
